import React, { createContext, useState, useEffect } from 'react';
import { navigate } from 'gatsby';
import { stripTime } from '../utils/Date';
import { getLocationHighRatingSpas, onClickDataLayer } from '../utils';
import { appInsights } from '../utils/AppInsights';

const defaultValues = {
  isOverlayActive: false,
  setOverlayActive: () => {},

  recentlyViewed: null,
  isSpaInRecentlyViewed: () => {},
  addSpaToRecentlyViewed: () => {},
  addLocationHighRatingSpasToRecentlyViewed: () => {},

  wishlist: null,
  isSpaInWishlist: () => {},
  toggleSpaInWishlist: () => {},

  cartItemCount: 0,

  configureBooking: () => {},
  getVoucherThemes: () => {},
  getBookingConfiguration: () => {},
  addToCart: () => {},
  lineItemConfigured: () => {},
  getCart: () => {},
  deleteCart: () => {},
  getCartItem: () => {},
  refreshCartItem: () => {},
  getCartItemCount: () => {},
  updateCartItem: () => {},
  removeFromCart: () => {},
  updateItemQuantity: () => {},
  getDiscounts: () => {},
  addDiscount: () => {},
  removeDiscount: () => {},
  getVouchers: () => {},
  addVoucher: () => {},
  removeVoucher: () => {},
  removeExternalVoucher: () => {},
  getPurchase: () => {},
  updatePurchase: () => {},
  savePurchaseBillingAddress: () => {}, 

  getDeliveryMethods: () => {},
  postcodeLookup: () => {},
  getCheckout: () => {},
  saveCheckout: () => {},
  saveBillingAddress: () => {},
  saveCustomerDetails: () => {},
  getCustomerDetails: () => {},

  createOrder: () => {},
  completeOrder: () => {},
  getOrder: () => {},

  getPayment: () => {},
  getPaypalContext: () => {},
  createPayment: () => {},
  createPaypalPayment: () => {},
  createPaypalContext: () => {},
  createPaypalContextPayment: () => {},
  validateApplePay: () => {},
  createApplePayPayment: () => {},
  paymentSuccess: () => {},
  paymentFailed: () => {},

  getVatReceipt: () => {},

  activateVoucher: () => {},

  validateLoginShell: () => {},
  validateLogin: () => {},
  accountAuth: () => {},
  getUpcomingBookings: () => {},
  getPreviousBookings: () => {},

  paymentsMetadata: () => {},
  paymentsSession: () => {},

  loaderActive: false,

  cjAffiliate: null,
  triggerCjAffiliatePageView: () => {},
};

export const StoreContext = createContext(defaultValues);

export const StoreProvider = ({ children }) => {
  const [isOverlayActive, setOverlayActive] = useState(
    defaultValues.isOverlayActive,
  );
  const [recentlyViewed, setRecentlyViewed] = useState(
    defaultValues.recentlyViewed,
  );
  const [wishlist, setWishlist] = useState(defaultValues.wishlist);
  const [cartItemCount, setCartItemCount] = useState(
    defaultValues.cartItemCount,
  );
  const [loaderActive, setLoaderActive] = useState(defaultValues.loaderActive);
  const [cjAffiliate, setCjAffiliate] = useState(defaultValues.cjAffiliate);

  const isBrowser = typeof window !== 'undefined';
  const localStorageRecentlyViewedKey = `spaseekers:recently-viewed`;
  const localStorageWishlistKey = `spaseekers:wishlist`;
  const localStorageUserIdKey = `spaseekers:user-id`;

  useEffect(() => {
    initialiseRecentlyViewed();
    initialiseWishlist();
    initialiseCheckout();
  }, []);

  // Recently Viewed

  const initialiseRecentlyViewed = () => {
    try {
      const recentlyViewed =
        isBrowser &&
        JSON.parse(localStorage.getItem(localStorageRecentlyViewedKey))
          ? JSON.parse(localStorage.getItem(localStorageRecentlyViewedKey))
          : isBrowser
            ? []
            : null;

      if (recentlyViewed) {
        setRecentlyViewed(recentlyViewed);
      }
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    }
  };

  const isSpaInRecentlyViewed = spa =>
    recentlyViewed && recentlyViewed.find(item => item.spaId === spa.spaId);

  const updateRecentlyViewed = updatedRecentlyViewed => {
    setRecentlyViewed(updatedRecentlyViewed);

    localStorage.setItem(
      localStorageRecentlyViewedKey,
      JSON.stringify(updatedRecentlyViewed),
    );

    appInsights.trackEvent({ name: 'UpdateRecentlyViewed' });
  };

  const addSpaToRecentlyViewed = spa => {
    if (recentlyViewed && !isSpaInRecentlyViewed(spa)) {
      const updatedRecentlyViewed = [spa, ...recentlyViewed.slice(0, 3)];
      updateRecentlyViewed(updatedRecentlyViewed);
    }
  };

  const addLocationHighRatingSpasToRecentlyViewed = async locationID => {
    const locationHighRatingSpas = await getLocationHighRatingSpas(locationID);
    updateRecentlyViewed(locationHighRatingSpas);
  };

  // Wishlist

  const initialiseWishlist = () => {
    try {
      const wishlist =
        isBrowser && JSON.parse(localStorage.getItem(localStorageWishlistKey))
          ? JSON.parse(localStorage.getItem(localStorageWishlistKey))
          : isBrowser
            ? []
            : null;

      if (wishlist) {
        setWishlist(wishlist);
      }
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    }
  };

  const isSpaInWishlist = spa =>
    wishlist && wishlist.find(item => item.spaId === spa.spaId);

  const updateWishlist = updatedWishlist => {
    setWishlist(updatedWishlist);

    localStorage.setItem(
      localStorageWishlistKey,
      JSON.stringify(updatedWishlist),
    );

    appInsights.trackEvent({ name: 'UpdateWishList' });
  };

  const toggleSpaInWishlist = spa => {
    let updatedWishlist = [];

    if (isSpaInWishlist(spa)) {
      updatedWishlist = wishlist.filter(item => item.spaId !== spa.spaId);
    } else {
      updatedWishlist = [...wishlist, spa];
    }

    updateWishlist(updatedWishlist);
  };

  // Checkout API

  const initialiseCheckout = async () => {
    appInsights.startTrackEvent('InitialiseCheckout');
    try {
      let cartItemCount = 0;

      const cartItemCountResult = await getCartItemCount();

      if (cartItemCountResult.ok() && cartItemCountResult.data) {
        cartItemCount = cartItemCountResult.data.itemCount || 0;
        setCartItemCount(cartItemCount);
      }

      const userId =
        isBrowser && localStorage.getItem(localStorageUserIdKey)
          ? localStorage.getItem(localStorageUserIdKey)
          : null;

      if (userId) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'sign_up',
          user_id: userId,
        });
      }
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('InitialiseCheckout');
    }
  };

  const fetchWithRetry = (url, options, resolve, attempt, attempts) => {
    const wait = delay =>
      new Promise(resolve => {
        setTimeout(resolve, delay);
      });
    const retry = attempt => {
      console.warn(
        `Retrying call to ${url} due to previous failure (attempt ${attempt} of ${attempts})`,
      );
      return wait(Math.pow(2, attempt) * 50).then(() =>
        fetchWithRetry(url, options, resolve, attempt, attempts),
      );
    };
    const parse = response => {
      try {
        response
          .json()
          .then(json => {
            resolve({
              status: response.ok ? 'OK' : 'FAILED',
              data: json?.data,
              message: json?.error || json?.message,
              error: !response.ok,
              ok: () => !!response.ok,
            });
          })
          .catch(error => {
            // Likely an issue with a malformed body
            resolve({
              status: 'FAILED',
              message: error.message,
              error: true,
              ok: () => false,
            });
          });
      } catch (error) {
        // Hard error if promise catch is not called
        resolve({
          status: 'FAILED',
          message: error.message,
          error: true,
          ok: () => false,
        });
      }
    };

    options.headers.set('Attempt', attempt);

    return fetch(url, options)
      .then(response => {
        if (response.status === 500) {
          if (attempt < attempts) {
            // Server errors, let's retry if we can
            return retry(attempt + 1);
          }
        }
        parse(response);
      })
      .catch(error => {
        // Generally network errors would end up here, so let's retry.
        if (attempt >= attempts) {
          resolve({
            status: 'FAILED',
            message: error.message,
          });
        } else {
          return retry(attempt + 1);
        }
      });
  };

  const checkoutRequest = (
    method,
    endpoint,
    data,
    token = '',
    referer = '',
    attempts = 3,
  ) => {
    let requestHeaders = new Headers();
    requestHeaders.append('Content-Type', 'application/json');

    if (referer) {
      requestHeaders.append('Referer', `${referer}`);
    }

    if (token) {
      requestHeaders.append('Authorization', `Bearer ${token}`);
    }

    // Generate an idempotency key so duplicate requests (because of retries) can be handled
    if (window.crypto && data && Object.keys(data).length > 0) {
      data = { idempotencyKey: window.crypto.randomUUID(), ...data };
    }

    const requestOptions = {
      method: method,
      headers: requestHeaders,
      body:
        data && Object.keys(data).length > 0 ? JSON.stringify(data) : undefined,
      credentials: 'include',
    };

    return new Promise(resolve => {
      fetchWithRetry(
        `${process.env.GATSBY_SPASEEKERS_CHECKOUT_API_URL}/${endpoint}`,
        requestOptions,
        resolve,
        1,
        attempts || 3,
      );
    });
  };

  const paymentsRequest = (
    method,
    endpoint,
    data,
    token = '',
    referer = '',
    attempts = 3,
  ) => {
    let requestHeaders = new Headers();
    requestHeaders.append('Content-Type', 'application/json');

    if (referer) {
      requestHeaders.append('Referer', `${referer}`);
    }

    // Generate an idempotency key so duplicate requests (because of retries) can be handled
    if (window.crypto && data && Object.keys(data).length > 0) {
      data = { idempotencyKey: window.crypto.randomUUID(), ...data };
    }

    const requestOptions = {
      method: method,
      headers: requestHeaders,
      body:
        data && Object.keys(data).length > 0 ? JSON.stringify(data) : undefined,
      credentials: 'include',
    };

    return new Promise(resolve => {
      fetchWithRetry(
        `${process.env.GATSBY_SPASEEKERS_PAYMENTS_API_URL}/${endpoint}`,
        requestOptions,
        resolve,
        1,
        attempts || 3,
      );
    });
  };

  const configureBooking = async ({
    spaId,
    packageId,
    numberOfRooms,
    numberOfGuests,
    arrivalDate,
    bookingOptions,
    upgrades,
  }) => {
    appInsights.startTrackEvent('ConfigureBooking');
    try {
      // Format data and send to request api
      const data = {
        spaId: spaId,
        packageId: packageId,
        numberOfRooms: numberOfRooms ? numberOfRooms : 0,
        numberOfGuests: numberOfGuests,
        arrivalDate: stripTime(arrivalDate),
        bookingOptions: bookingOptions,
        upgrades: upgrades,
      };

      const configuredBooking = await checkoutRequest(
        'POST',
        'v1/cart/configure-booking',
        data,
      );

      if (configuredBooking.error) {
        setLoaderActive(false);
        navigate('/500/', { replace: true });
      }

      return configuredBooking;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('ConfigureBooking');
    }
  };

  const getVoucherThemes = async () => {
    appInsights.startTrackEvent('GetVoucherThemes');
    try {
      const themes = await checkoutRequest('GET', `v1/cart/themes`, {});

      return themes;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetVoucherThemes');
    }
  };

  const getBookingConfiguration = async cartItemId => {
    appInsights.startTrackEvent('GetBookingConfiguration');
    try {
      const configuredBooking = await checkoutRequest(
        'GET',
        `v1/cart/configure-booking/${cartItemId}`,
        {},
      );

      return configuredBooking;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetBookingConfiguration');
    }
  };

  const addToCart = async cartItem => {
    appInsights.startTrackEvent('AddToCart');
    try {
      const cart = await checkoutRequest(
        'POST',
        'v1/cart/item',
        cartItem,
        '',
        '',
        1,
      );

      if (cart && cart.ok()) {
        setCartItemCount(cartItemCount + cart.data.quantity);
      }

      return cart;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
      navigate('/500/', { replace: true });
    } finally {
      appInsights.stopTrackEvent('AddToCart');
    }
  };

  const lineItemConfigured = async cartItemId => {
    appInsights.startTrackEvent('LineItemConfigured');
    try {
      const cartItem = await checkoutRequest(
        'POST',
        `v1/cart/item/${cartItemId}/configured`,
        {},
      );

      return cartItem;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('LineItemConfigured');
    }
  };

  const getCart = async () => {
    appInsights.startTrackEvent('GetCart');
    try {
      const cart = await checkoutRequest('GET', 'v1/cart', {});
      if (cart.ok() && cart.data) {
        const cartItemCount = cart.data.itemCount || 0;
        setCartItemCount(cartItemCount);

        if (cart.data.cjUniversalAffiliate) {
          setCjAffiliate(cart.data.cjUniversalAffiliate);
        }

        if (cartItemCount === 0) {
          navigate('/checkout/basket/');
        }
      }

      return cart;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetCart');
    }
  };

  const deleteCart = async () => {
    appInsights.startTrackEvent('DeleteCart');
    try {
      const cart = await checkoutRequest('DELETE', 'v1/cart', {});
      if (cart.ok()) {
        setCartItemCount(0);
      }
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('DeleteCart');
    }
  };

  const getCartItem = async cartItemId => {
    appInsights.startTrackEvent('GetCartItem');
    try {
      const cart = await checkoutRequest(
        'GET',
        `v1/cart/item/${cartItemId}`,
        {},
      );

      return cart;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetCartItem');
    }
  };

  const refreshCartItem = async cartItemId => {
    appInsights.startTrackEvent('RefreshCartItem');
    try {
      const cart = await checkoutRequest(
        'GET',
        `v1/cart/item/${cartItemId}/refresh`,
        {},
      );

      return cart;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('RefreshCartItem');
    }
  };

  const getCartItemCount = async () => {
    appInsights.startTrackEvent('GetCartItemCount');
    try {
      const cart = await checkoutRequest('GET', `v1/cart/item-count`, {});
      if (cart && cart.data && cart.data.cjUniversalAffiliate) {
        setCjAffiliate(cart.data.cjUniversalAffiliate);
      }

      return cart;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetCartItemCount');
    }
  };

  const updateCartItem = async (cartItemId, data) => {
    appInsights.startTrackEvent('UpdateCartItem');
    try {
      const cart = await checkoutRequest(
        'PUT',
        `v1/cart/item/${cartItemId}`,
        data,
      );

      return cart;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('UpdateCartItem');
    }
  };

  const removeFromCart = async cartItemId => {
    appInsights.startTrackEvent('RemoveFromCart');
    try {
      const cart = await checkoutRequest(
        'DELETE',
        `v1/cart/item/${cartItemId}`,
        {},
      );

      return cart;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('RemoveFromCart');
    }
  };

  const updateItemQuantity = async (cartItemId, quantity) => {
    appInsights.startTrackEvent('UpdateItemQuantity');
    try {
      const cart = await checkoutRequest(
        'PUT',
        `v1/cart/item/${cartItemId}/quantity`,
        { quantity: quantity },
      );

      return cart;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('UpdateItemQuantity');
    }
  };

  const getDiscounts = async () => {
    appInsights.startTrackEvent('GetDiscounts');
    try {
      const discounts = await checkoutRequest('GET', `v1/cart/discounts`, {});

      return discounts;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetDiscounts');
    }
  };

  const addDiscount = async code => {
    appInsights.startTrackEvent('AddDiscount');
    try {
      const discounts = await checkoutRequest(
        'POST',
        `v1/cart/discounts`,
        {
          code: code,
        },
        '',
        '',
        1,
      );

      return discounts;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('AddDiscount');
    }
  };

  const removeDiscount = async discountId => {
    appInsights.startTrackEvent('RemoveDiscount');
    try {
      const discounts = await checkoutRequest(
        'DELETE',
        `v1/cart/discounts/${discountId}`,
        {},
      );

      return discounts;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('RemoveDiscount');
    }
  };

  const getVouchers = async () => {
    appInsights.startTrackEvent('GetVouchers');
    try {
      const vouchers = await checkoutRequest(
        'GET',
        `v1/checkout/gift-options`,
        {},
      );

      return vouchers;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetVouchers');
    }
  };

  const addVoucher = async (urn, surname, value) => {
    appInsights.startTrackEvent('AddVoucher');
    try {
      const vouchers = await checkoutRequest(
        'POST',
        `v1/cart/vouchers`,
        {
          urn: urn,
          surname: surname,
          value: value,
        },
        '',
        '',
        1,
      );

      return vouchers;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('AddVoucher');
    }
  };

  const removeVoucher = async voucherId => {
    appInsights.startTrackEvent('RemoveVoucher');
    try {
      const vouchers = await checkoutRequest(
        'DELETE',
        `v1/cart/vouchers/${voucherId}`,
        {},
      );

      return vouchers;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('RemoveVoucher');
    }
  };

  const getPurchase = async data => {
    appInsights.startTrackEvent('GetPurchase');
    try {
      const purchase = await checkoutRequest(
        'GET',
        `v1/purchases/?${data.toString()}`,
        {},
        '',
        '',
        1,
      );

      return purchase;
    } catch (e) {
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetPurchase');
    }
  };

  const updatePurchase = async (data, params) => {
    appInsights.startTrackEvent('UpdatePurchase');
    try {
      const purchase = await checkoutRequest(
        'POST',
        `v1/purchases/?${params.toString()}`,
        data,
        '',
        '',
        1,
      );

      return purchase;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('UpdatePurchase');
    }
  };

  const savePurchaseBillingAddress = async (data,params) => {
    appInsights.startTrackEvent('SavePurchaseBillingAddress');
    try {
      const checkout = await checkoutRequest(
        'POST', 
        `v1/purchases/billing-address?${params.toString()}`,
        data);

      return checkout;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('SavePurchaseBillingAddress');
    }
  }

  const removeExternalVoucher = async voucherId => {
    appInsights.startTrackEvent('RemoveExternalVoucher');
    try {
      const vouchers = await checkoutRequest(
        'DELETE',
        `v1/cart/vouchers/external/${voucherId}`,
        {},
      );

      return vouchers;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    }
    appInsights.stopTrackEvent('RemoveExternalVoucher');
  };

  const getDeliveryMethods = async () => {
    appInsights.startTrackEvent('GetDeliveryMethods');
    try {
      const deliveryMethods = await checkoutRequest(
        'GET',
        `v1/checkout/delivery-methods`,
        {},
      );

      return deliveryMethods;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetDeliveryMethods');
    }
  };

  const postcodeLookup = async postcode => {
    appInsights.startTrackEvent('PostcodeLookup');
    try {
      // Remove any trailing spaces (mobile autofill)
      const lookup = await checkoutRequest(
        'GET',
        `v1/checkout/addresses/${encodeURI(postcode.trim())}`,
        {},
        '',
        process.env.GATSBY_SPASEEKERS_POSTCODE_LOOKUP_API_URL,
      );

      return lookup;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('PostcodeLookup', { postcode });
    }
  };

  const getCheckout = async data => {
    appInsights.startTrackEvent('GetCheckout');
    try {
      const checkout = await checkoutRequest('GET', 'v1/checkout', {});

      if (
        checkout.data &&
        checkout.data.customerDetails &&
        checkout.data.customerDetails.tracking
      ) {
        if (
          !localStorage.getItem(localStorageUserIdKey) ||
          localStorage.getItem(localStorageUserIdKey) !==
            checkout.data.customerDetails.tracking.userId
        ) {
          localStorage.setItem(
            localStorageUserIdKey,
            checkout.data.customerDetails.tracking.userId,
          );

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'sign_up',
            user_id: checkout.data.customerDetails.tracking.userId,
          });
        }
      }

      return checkout;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetCheckout');
    }
  };

  const saveCheckout = async data => {
    appInsights.startTrackEvent('SaveCheckout');
    try {
      const checkout = await checkoutRequest('POST', 'v1/checkout', data);

      return checkout;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('SaveCheckout');
    }
  };

  const saveBillingAddress = async data => {
    appInsights.startTrackEvent('SaveBillingAddress ');
    try {
      const checkout = await checkoutRequest(
        'POST', 
        'v1/checkout/billing-address', 
        data);

      return checkout;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('SaveBillingAddress ');
    }
  };

  const saveCustomerDetails = async data => {
    appInsights.startTrackEvent('SaveCustomerDetails');
    try {
      const checkout = await checkoutRequest(
        'POST',
        'v1/checkout/customer-details',
        data,
      );

      return checkout;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('SaveCustomerDetails');
    }
  };

  const getCustomerDetails = async () => {
    appInsights.startTrackEvent('GetCustomerDetails');
    try {
      const checkout = await checkoutRequest(
        'GET',
        'v1/checkout/customer-details',
        {},
      );

      return checkout;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetCustomerDetails');
    }
  };

  const createOrder = async () => {
    appInsights.startTrackEvent('CreateOrder');
    try {
      const order = await checkoutRequest('POST', 'v1/order', {}, '', '', 1);

      return order;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('CreateOrder');
    }
  };

  const completeOrder = async () => {
    appInsights.startTrackEvent('CompleteOrder');
    try {
      const result = await checkoutRequest('POST', 'v1/order/complete', {});

      return result;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('CompleteOrder');
    }
  };

  const getOrder = async orderToken => {
    appInsights.startTrackEvent('GetOrder');
    try {
      const order = await checkoutRequest('GET', `v1/order/${orderToken}`, {});
      if (order && order.data && order.data.cjUniversalAffiliate) {
        setCjAffiliate(order.data.cjUniversalAffiliate);
      }

      return order;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetOrder');
    }
  };

  const getPayment = async () => {
    appInsights.startTrackEvent('GetPayment');
    try {
      const payment = await checkoutRequest('GET', 'v1/payment', {});

      return payment;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetPayment');
    }
  };

  const getPaypalContext = async (paymentContextId, orderId) => {
    appInsights.startTrackEvent('GetPayPalContext');
    try {
      const context = await checkoutRequest(
        'GET',
        `v1/payment/paypal/context?paymentContextId=${paymentContextId}${!!orderId ? `&orderId=${orderId}` : ``}`,
        undefined,
        '',
        '',
        1,
      );

      return context;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetPayPalContext');
    }
  };

  const createPayment = async data => {
    appInsights.startTrackEvent('CreatePayment');
    try {
      const payment = await checkoutRequest(
        'POST',
        'v1/payment',
        data,
        '',
        '',
        1,
      );

      return payment;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('CreatePayment');
    }
  };

  const createPaypalPayment = async () => {
    appInsights.startTrackEvent('CratePayPalPayment');
    try {
      const payment = await checkoutRequest(
        'POST',
        'v1/payment/paypal',
        {},
        '',
        '',
        1,
      );

      return payment;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('CreatePayPalPayment');
    }
  };

  const createPaypalContext = async data => {
    appInsights.startTrackEvent('CreatePayPalContext');
    try {
      const context = await checkoutRequest(
        'POST',
        'v1/payment/paypal/context',
        data,
        '',
        '',
        1,
      );

      return context;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('CreatePayPalContext');
    }
  };

  const createPaypalContextPayment = async (paymentContextId, orderId) => {
    appInsights.startTrackEvent('CreatePayPalContextPayment');
    try {
      const payment = await checkoutRequest(
        'POST',
        `v1/payment/paypal/payment?paymentContextId=${paymentContextId}${!!orderId ? `&orderId=${orderId}` : ``}`,
        undefined,
        '',
        '',
        1,
      );

      return payment;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('CreatePayPalContextPayment');
    }
  };

  const validateApplePay = async data => {
    appInsights.startTrackEvent('ValidateApplePay');
    try {
      const applePay = await checkoutRequest(
        'POST',
        'v1/payment/apple-pay/validate',
        data,
      );

      return applePay;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('ValidateApplePay');
    }
  };

  const createApplePayPayment = async data => {
    appInsights.startTrackEvent('CreateApplePayPayment');
    try {
      const applePay = await checkoutRequest(
        'POST',
        'v1/payment/apple-pay/payment',
        data,
      );

      return applePay;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('CreateApplePayPayment');
    }
  };

  const paymentSuccess = async data => {
    appInsights.startTrackEvent('PaymentSuccess');
    try {
      const payment = await checkoutRequest('POST', 'v1/payment/success', data);

      return payment;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('PaymentSuccess');
    }
  };

  const paymentFailed = async data => {
    appInsights.startTrackEvent('PaymentFailed');
    try {
      const payment = await checkoutRequest('POST', 'v1/payment/failure', data);

      return payment;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('PaymentFailed');
    }
  };

  const getVatReceipt = async orderToken => {
    appInsights.startTrackEvent('GetVATReceipt');
    try {
      return `${process.env.GATSBY_SPASEEKERS_CHECKOUT_API_URL}/v1/order/${orderToken}/vat-receipt`;
    } catch (e) {
      appInsights.trackException({ exception: e });
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('GetVATReceipt');
    }
  };

  const activateVoucher = async data => {
    appInsights.startTrackEvent('ActivateVoucher');
    try {
      const activate = await checkoutRequest(
        'POST',
        'v1/voucher/activate',
        data,
        '',
        '',
        1,
      );

      return activate;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('ActivateVoucher');
    }
  };

  const validateLoginShell = async data => {
    appInsights.startTrackEvent('ValidateLoginShell');
    try {
      const validate = await checkoutRequest(
        'GET',
        `v1/purchases/validate?${data.toString()}`,
        {},
        '',
        1,
      );

      return validate;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('ValidateLoginShell');
    }
  };

  const validateLogin = async token => {
    appInsights.startTrackEvent('ValidateLogin');
    try {
      const validate = await checkoutRequest(
        'GET',
        `v1/account/me`,
        {},
        token,
        '',
        1,
      );

      return validate;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('ValidateLogin');
    }
  };

  const accountAuth = async email => {
    appInsights.startTrackEvent('AccountAuth');
    try {
      const auth = await checkoutRequest(
        'POST',
        `v1/account/auth`,
        {
          email: email,
        },
        '',
        '',
        1,
      );

      return auth;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('AccountAuth');
    }
  };

  const getUpcomingBookings = async token => {
    appInsights.startTrackEvent('UpcomingBookings');
    try {
      const bookings = await checkoutRequest(
        'GET',
        `v1/purchases/upcoming`,
        {},
        token,
        '',
        1,
      );

      return bookings;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('UpcomingBookings');
    }
  };

  const getPreviousBookings = async token => {
    appInsights.startTrackEvent('PreviousBookings');
    try {
      const bookings = await checkoutRequest(
        'GET',
        `v1/purchases/previous`,
        {},
        token,
        '',
        1,
      );

      return bookings;
    } catch (e) {
      appInsights.trackException({ exception: e });
      setLoaderActive(false); // fallback to remove loader
      console.error(e);
    } finally {
      appInsights.stopTrackEvent('PreviousBookings');
    }
  };

  const triggerCjAffiliatePageView = (pageType, customData) => {
    onClickDataLayer({
      customData: {
        event: 'cj-universal-page-view',
        cj_universal_page_type: pageType || 'information',
        cj_universal_user_id: !!cjAffiliate ? cjAffiliate.userId : undefined,
        cj_universal_email_hash: !!cjAffiliate
          ? cjAffiliate.emailHash
          : undefined,
        cj_universal_subtotal: !!cjAffiliate ? cjAffiliate.subtotal : undefined,
        ...(customData || {}),
      },
    });
  };

  const paymentsMetadata = async () => {
    appInsights.startTrackEvent('PaymentsMetadata');
    try {
      const metadata = await paymentsRequest('GET', `metadata`);

      return metadata;
    } catch (e) {
      appInsights.trackException({ exception: e });
    } finally {
      appInsights.stopTrackEvent('PaymentsMetadata');
    }
  };

  const paymentsSession = async (request) => {
    appInsights.startTrackEvent('PaymentSession');
    try {
      const session = await paymentsRequest('POST', `session`, request);

      return session;
    } catch (e) {
      appInsights.trackException({ exception: e });
    } finally {
      appInsights.stopTrackEvent('PaymentSession');
    }
  };

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,

        isOverlayActive,
        setOverlayActive,

        recentlyViewed,
        isSpaInRecentlyViewed,
        addSpaToRecentlyViewed,
        addLocationHighRatingSpasToRecentlyViewed,

        wishlist,
        isSpaInWishlist,
        toggleSpaInWishlist,

        cartItemCount,

        setCartItemCount,
        configureBooking,
        getBookingConfiguration,
        getVoucherThemes,
        addToCart,
        lineItemConfigured,
        getCart,
        deleteCart,
        getCartItem,
        refreshCartItem,
        getCartItemCount,
        updateCartItem,
        removeFromCart,
        updateItemQuantity,
        getDiscounts,
        addDiscount,
        removeDiscount,
        getVouchers,
        addVoucher,
        removeVoucher,
        removeExternalVoucher,
        getPurchase,
        updatePurchase,
        savePurchaseBillingAddress,

        getDeliveryMethods,
        postcodeLookup,
        getCheckout,
        saveCheckout, 
        saveBillingAddress,
        saveCustomerDetails,
        getCustomerDetails,

        createOrder,
        completeOrder,
        getOrder,

        getPayment,
        getPaypalContext,
        createPayment,
        createPaypalPayment,
        createPaypalContext,
        createPaypalContextPayment,
        validateApplePay,
        createApplePayPayment,
        paymentSuccess,
        paymentFailed,

        getVatReceipt,

        activateVoucher,

        validateLoginShell,
        validateLogin,
        accountAuth,
        getUpcomingBookings,
        getPreviousBookings,

        loaderActive,
        setLoaderActive,

        cjAffiliate,
        triggerCjAffiliatePageView,

        paymentsMetadata,
        paymentsSession
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};
